import type { Container } from 'pixi.js';
import type { Vec2 } from '@nanocars/shared';

// 0.15s smoothing time constant: the fraction of the remaining gap closed
// per second is 1/EASE_TIME_S, applied per-frame as min(1, rate * dt) so it
// stays frame-rate independent (matches the style of race.ts's own
// CAMERA_EASE_RATE on the server).
const EASE_TIME_S = 0.15;
const EASE_RATE = 1 / EASE_TIME_S;

// Pans (never rotates or zooms) a world container so that a given world
// position sits at the centre of the logical screen, easing toward a moving
// target instead of snapping straight to it every frame. Zoom is fixed by
// the caller setting world.scale once (pxPerM); Camera only ever touches
// world.x/world.y.
export class Camera {
  private pos: Vec2;

  constructor(
    private readonly world: Container,
    private readonly pxPerM: number,
    private readonly screenW: number,
    private readonly screenH: number,
    initial: Vec2,
  ) {
    this.pos = { ...initial };
    this.apply();
  }

  update(target: Vec2, dt: number): void {
    const t = Math.min(1, EASE_RATE * dt);
    this.pos.x += (target.x - this.pos.x) * t;
    this.pos.y += (target.y - this.pos.y) * t;
    this.apply();
  }

  /** Snaps straight to a position with no easing (e.g. on scene enter). */
  snapTo(pos: Vec2): void {
    this.pos = { ...pos };
    this.apply();
  }

  get worldPos(): Vec2 {
    return { ...this.pos };
  }

  private apply(): void {
    this.world.x = this.screenW / 2 - this.pos.x * this.pxPerM;
    this.world.y = this.screenH / 2 - this.pos.y * this.pxPerM;
  }
}
